import React from 'react';
import {
  makeStyles,
  mergeClasses,
  shorthands,
  tokens,
  Text,
} from '@fluentui/react-components';
import { LockClosed12Filled, VehicleTruck20Filled } from '@fluentui/react-icons';
import {
  computeTimeline,
  timelineBoundaries,
  clampBoundaryDate,
  TimelineSegmentInput,
  LaidOutSegment,
  TimelineBoundary,
} from '../lib/phaseTimeline';
import { addDays, applyPhaseEdit, EditablePhase } from '../lib/phaseDates';

// Timeline strip — a non-linear-editor style overview that sits above the phase
// cards. Each phase is a segment whose width is proportional to its duration; a
// "today" playhead marks the current day.
//
// Read-only by default. When `onBoundaryEdit` is supplied (the planning stage on
// a project), the segment boundaries become draggable / keyboard-editable
// handles: dragging a seam moves that swap hinge, and the two outer edges move
// the project's start (slides the whole chain) and end (resizes the last phase).
// Every move is a single (phaseId, edge, date) edit fed back to the caller, which
// runs it through applyPhaseEdit (ripple). A promoted phase's edges DO move here
// — staff reschedule a booked phase from this timeline and the change is pushed
// to its Booqable order on save. Only the hinge between two promoted phases is
// locked (can't move both reservations at once). When an edit absorbs against a
// promoted wall (ripple stops there), the boundary is clamped so it can't invert
// that pinned promoted window.
//
// During a drag the strip resizes its own segments in realtime by applying the
// in-progress edit to a LOCAL copy of the chain (same applyPhaseEdit/ripple the
// parent uses), so the widths reflow live under the cursor. We still defer the
// single commit to the parent until release — that keeps the phase-card date
// pickers and the debounced auto-save off the per-pixel path. Because the local
// preview uses the same math, the last drag frame is identical to the committed
// result: no snap on release.

// Status → accent. Each segment is a white capsule bookended by accent-colored
// grip caps with a matching border; the accent carries status at a glance while
// the label/meta stay dark on white. Draft (and anything unmapped) defaults to
// brand — so a fresh schedule reads as the brand teal in the screenshot, not a
// muted grey. Uses BorderActive tokens (guaranteed to exist for palette colors).
const STATUS_ACCENT: Record<string, string> = {
  reserved: tokens.colorBrandBackground,
  started: tokens.colorPaletteMarigoldBorderActive,
  stopped: tokens.colorPaletteGreenBorderActive,
};
const accentFor = (status?: string | null) =>
  (status && STATUS_ACCENT[status.toLowerCase()]) || tokens.colorBrandBackground;

// Format a YYYY-MM-DD string as "Jun 1" without timezone drift (parse as UTC,
// render in UTC).
const fmtYmd = (ymd: string): string => {
  const [y, m, d] = ymd.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

const useStyles = makeStyles({
  wrap: {
    display: 'flex',
    flexDirection: 'column',
    rowGap: '6px',
  },
  track: {
    display: 'flex',
    alignItems: 'stretch',
    // 56px of capsule height + the vertical padding below (10 + 4) so the
    // segments still stretch to 56px after the playhead room is added.
    minHeight: '70px',
    width: '100%',
    // On narrow screens the segments (each min 96px) overrun the viewport and
    // drag the whole page wide. Scroll the strip horizontally inside its panel
    // instead. The vertical padding gives the "today" playhead dot — which pokes
    // ~9px above the track — room so the horizontal-scroll clip doesn't cut it
    // off; the matching negative margin borrows that room from the gap above/
    // below so the panel layout is unchanged. On desktop the segments flex-fill
    // the width, so no scrollbar appears.
    ...shorthands.overflow('auto', 'hidden'),
    ...shorthands.padding('10px', '0', '4px', '0'),
    ...shorthands.margin('-10px', '0', '-4px', '0'),
  },
  // A self-contained phase capsule: accent grip caps bookend a white, centered
  // body, all inside a rounded accent border. Caps + border color come in via
  // inline style (status accent). overflow:hidden clips the caps to the radius.
  segment: {
    position: 'relative',
    flexBasis: 0,
    minWidth: '96px',
    display: 'flex',
    alignItems: 'stretch',
    ...shorthands.borderRadius('8px'),
    ...shorthands.overflow('hidden'),
    backgroundColor: tokens.colorNeutralBackground1,
    // The capsule reads as a control (boundaries drag the seams), not prose —
    // a text selection here is always accidental, so suppress it.
    userSelect: 'none',
  },
  // Accent end-cap holding a white grip pill — purely decorative now; the drag
  // target is the gap between two segments' caps (see boundary).
  cap: {
    flexShrink: 0,
    width: '20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  gripPill: {
    width: '4px',
    height: '46%',
    minHeight: '14px',
    ...shorthands.borderRadius('2px'),
    backgroundColor: tokens.colorNeutralForegroundOnBrand,
  },
  body: {
    position: 'relative',
    flexGrow: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    rowGap: '2px',
    ...shorthands.padding('4px', '8px'),
    ...shorthands.overflow('hidden'),
  },
  segmentLabel: {
    display: 'flex',
    alignItems: 'center',
    columnGap: '4px',
    maxWidth: '100%',
    whiteSpace: 'nowrap',
    ...shorthands.overflow('hidden'),
    textOverflow: 'ellipsis',
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase400,
    lineHeight: tokens.lineHeightBase400,
  },
  segmentMeta: {
    whiteSpace: 'nowrap',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  // Drag target for a boundary — the margin between two segments' caps (and the
  // outer margins for project start/end). A thin grip line, invisible until the
  // zone is hovered/focused/dragged (driven by currentColor), so the resting
  // strip stays clean.
  boundary: {
    flexShrink: 0,
    width: '16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'transparent',
  },
  boundaryDraggable: {
    cursor: 'ew-resize',
    touchAction: 'none',
    ':hover': { color: tokens.colorBrandStroke1 },
    ':focus-visible': {
      color: tokens.colorBrandStroke1,
      ...shorthands.outline('2px', 'solid', tokens.colorStrokeFocus2),
      ...shorthands.borderRadius('3px'),
    },
  },
  boundaryGrip: {
    width: '2px',
    height: '60%',
    ...shorthands.borderRadius('2px'),
    backgroundColor: 'currentColor',
  },
  boundaryLock: {
    color: tokens.colorNeutralForeground4,
  },
  lock: {
    flexShrink: 0,
  },
  // Terminal marker for a pickup-only phase (the final pickup): an accent-ringed
  // truck pin capping the strip, with the collection date beneath. It's a point,
  // not a draggable segment — staff reschedule it from the phase card's pickup
  // date (the same swap hinge the project-end handle drives).
  marker: {
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    rowGap: '2px',
    minWidth: '52px',
    ...shorthands.padding('0', '0', '0', '4px'),
    userSelect: 'none',
  },
  markerPin: {
    width: '30px',
    height: '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.borderRadius('50%'),
    borderWidth: '1.5px',
    borderStyle: 'solid',
    backgroundColor: tokens.colorNeutralBackground1,
  },
  markerLabel: {
    whiteSpace: 'nowrap',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  // Vertical "today" line, positioned proportionally inside its segment.
  playhead: {
    position: 'absolute',
    top: '-3px',
    bottom: '-3px',
    width: '2px',
    backgroundColor: tokens.colorPaletteRedForeground1,
    pointerEvents: 'none',
  },
  playheadDot: {
    position: 'absolute',
    top: '-6px',
    left: '-3px',
    width: '8px',
    height: '8px',
    ...shorthands.borderRadius('50%'),
    backgroundColor: tokens.colorPaletteRedForeground1,
  },
  axis: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    columnGap: '8px',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  axisCenter: {
    color: tokens.colorNeutralForeground3,
  },
});

interface PhaseTimelineStripProps {
  /** Phases to lay out — usually the live (possibly edited) schedule joined
   *  with each phase's status/label. */
  segments: TimelineSegmentInput[];
  /** Current day as YYYY-MM-DD, for the playhead. Defaults to today (local). */
  todayYmd?: string;
  /** When provided, the segment boundaries become draggable / keyboard handles.
   *  Called with the phase edge to move and the new (already-clamped) date — the
   *  caller runs it through applyPhaseEdit. Omit for a read-only strip. */
  onBoundaryEdit?: (phaseId: string, edge: 'start' | 'end', date: string) => void;
}

const localTodayYmd = (): string => {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

// Drag steps in whole days; arrow keys nudge by 1, Shift+arrow by a week.
const KEY_STEP_WEEK = 7;

const PhaseTimelineStrip: React.FC<PhaseTimelineStripProps> = ({
  segments,
  todayYmd,
  onBoundaryEdit,
}) => {
  const styles = useStyles();
  const today = todayYmd ?? localTodayYmd();
  const editable = !!onBoundaryEdit;

  // Live drag edit. While a boundary is being dragged this holds the in-progress
  // (already clamped / day-quantized) edit; null when nothing is dragging. It's
  // applied to a LOCAL copy of the chain below so the strip reflows in realtime
  // without touching the parent until release.
  const [dragEdit, setDragEdit] = React.useState<{
    index: number;
    phaseId: string;
    edge: 'start' | 'end';
    date: string;
  } | null>(null);

  // The chain we actually render: the committed segments, or — mid-drag — those
  // segments with the in-progress edit applied via the same ripple math the
  // parent commits on release. applyPhaseEdit only reads EditablePhase fields and
  // spreads the rest, so label/status ride through untouched.
  const liveSegments = React.useMemo<TimelineSegmentInput[]>(() => {
    if (!dragEdit) return segments;
    return applyPhaseEdit(segments as EditablePhase[], {
      phaseId: dragEdit.phaseId,
      edge: dragEdit.edge,
      date: dragEdit.date,
      mode: 'ripple',
    }) as TimelineSegmentInput[];
  }, [segments, dragEdit]);

  const timeline = React.useMemo(
    () => computeTimeline(liveSegments, today),
    [liveSegments, today],
  );

  // Boundary list (B0..BN) keyed by index. boundaries[i] is the LEFT edge of
  // segment i; boundaries[i+1] its RIGHT edge. Rendered as edge-anchored handles
  // so they stay glued to the real flex seams without measuring widths.
  const boundaries = React.useMemo(
    () => timelineBoundaries(timeline.segments),
    [timeline.segments],
  );

  // Live drag state. pxPerDay + the origin date/x are captured at pointerdown so
  // the cursor→days conversion stays stable even as the strip reflows mid-drag
  // (we measure days off the cursor's displacement from startX, not the live
  // layout, so a growing chain never feeds back into the conversion).
  const dragRef = React.useRef<{
    boundary: TimelineBoundary;
    prevDate: string | null;
    maxDate: string | null;
    startX: number;
    pxPerDay: number;
    lastApplied: string;
  } | null>(null);

  // Rendered capsule elements by segment id, so a boundary (which now sits in the
  // gap between segments, not inside one) can measure its controlling segment's
  // width for the px-per-day conversion.
  const segRefs = React.useRef<Record<string, HTMLElement | null>>({});

  const prevDateOf = (b: TimelineBoundary): string | null =>
    b.index > 0 ? boundaries[b.index - 1].date : null;

  // Upper clamp for a boundary edit that will be ABSORBED against a promoted
  // phase to its right: ripple stops at that wall, so the moved boundary must
  // not cross the promoted phase's pinned far edge (which would invert it). The
  // phase to the right of boundary i is segment i; only promoted-and-pinned when
  // ripple can't slide its right edge. Null otherwise (ripple slides downstream,
  // or there's no phase to the right — the last boundary).
  const maxDateOf = (b: TimelineBoundary): string | null => {
    const rightSeg = timeline.segments[b.index];
    return rightSeg?.promoted && boundaries[b.index + 1]
      ? boundaries[b.index + 1].date
      : null;
  };

  const onHandlePointerDown = (
    e: React.PointerEvent<HTMLDivElement>,
    b: TimelineBoundary,
    controllingSeg: LaidOutSegment,
  ) => {
    if (!onBoundaryEdit || b.locked) return;
    e.preventDefault();
    e.stopPropagation();
    // controllingSeg is the segment whose duration this boundary changes; its
    // rendered width / days gives px-per-day (honours the min-width better than a
    // global average would).
    const segEl = segRefs.current[controllingSeg.id];
    const widthPx = segEl ? segEl.getBoundingClientRect().width : controllingSeg.days;
    const pxPerDay = Math.max(1, widthPx / controllingSeg.days);
    dragRef.current = {
      boundary: b,
      prevDate: prevDateOf(b),
      maxDate: maxDateOf(b),
      startX: e.clientX,
      pxPerDay,
      lastApplied: b.date,
    };
    setDragEdit({ index: b.index, phaseId: b.phaseId, edge: b.edge, date: b.date });
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onHandlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d || !onBoundaryEdit) return;
    const deltaDays = Math.round((e.clientX - d.startX) / d.pxPerDay);
    const next = clampBoundaryDate(d.prevDate, addDays(d.boundary.date, deltaDays), d.maxDate);
    d.lastApplied = next;
    // Push the in-progress edit into local preview state only — the strip reflows
    // live (segments resize under the cursor) while the parent, the card date
    // pickers and the debounced auto-save stay untouched until release.
    setDragEdit((prev) =>
      prev && prev.date === next
        ? prev
        : { index: d.boundary.index, phaseId: d.boundary.phaseId, edge: d.boundary.edge, date: next },
    );
  };

  const onHandlePointerUp = (e: React.PointerEvent<HTMLDivElement>) => {
    const d = dragRef.current;
    if (!d) return;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* capture may already be gone */
    }
    dragRef.current = null;
    setDragEdit(null);
    // Commit once, on release: this is the only edit the parent (and the card
    // date fields + auto-save) sees from a drag.
    if (onBoundaryEdit && d.lastApplied !== d.boundary.date) {
      onBoundaryEdit(d.boundary.phaseId, d.boundary.edge, d.lastApplied);
    }
  };

  const onHandleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, b: TimelineBoundary) => {
    if (!onBoundaryEdit || b.locked) return;
    let step = 0;
    if (e.key === 'ArrowLeft') step = -1;
    else if (e.key === 'ArrowRight') step = 1;
    else return;
    if (e.shiftKey) step *= KEY_STEP_WEEK;
    e.preventDefault();
    const next = clampBoundaryDate(prevDateOf(b), addDays(b.date, step), maxDateOf(b));
    if (next !== b.date) onBoundaryEdit(b.phaseId, b.edge, next);
  };

  // Nothing dated yet — let the cards carry the empty/partial state.
  if (timeline.segments.length === 0) return null;

  // The drag target for a boundary: the margin between two segments' caps (or the
  // outer margin, for project start/end). Read-only → an empty spacer that just
  // holds the gap; locked → a lock marker; otherwise a draggable/keyboard slider
  // whose grip slides with the cursor during a drag and commits on release.
  const renderBoundary = (
    b: TimelineBoundary,
    controllingSeg: LaidOutSegment,
    label: string,
  ) => {
    if (!editable) return <div key={`b${b.index}`} className={styles.boundary} aria-hidden />;
    if (b.locked) {
      return (
        <div key={`b${b.index}`} className={styles.boundary} aria-hidden>
          <LockClosed12Filled className={styles.boundaryLock} />
        </div>
      );
    }
    // b.date already reflects the in-progress edit mid-drag (the boundary list is
    // derived from the live preview chain), so the live reflow keeps the grip glued
    // to the real seam and the aria value current — no separate cap offset needed.
    const previewing = dragEdit?.index === b.index;
    return (
      <div
        key={`b${b.index}`}
        className={mergeClasses(styles.boundary, styles.boundaryDraggable)}
        style={previewing ? { color: tokens.colorBrandStroke1 } : undefined}
        role="slider"
        tabIndex={0}
        aria-label={label}
        aria-valuetext={fmtYmd(b.date)}
        aria-orientation="horizontal"
        onPointerDown={(e) => onHandlePointerDown(e, b, controllingSeg)}
        onPointerMove={onHandlePointerMove}
        onPointerUp={onHandlePointerUp}
        onPointerCancel={onHandlePointerUp}
        onKeyDown={(e) => onHandleKeyDown(e, b)}
      >
        <span className={styles.boundaryGrip} />
      </div>
    );
  };

  // One phase capsule: accent grip caps bookend a white, centered body. The caps
  // are decorative (the drag target is the boundary in the gap); the accent +
  // border carry status.
  const renderSegment = (s: LaidOutSegment) => {
    const accent = accentFor(s.status);
    const playhead =
      timeline.today && timeline.today.segmentId === s.id ? timeline.today.leftPct : null;
    const range = `${fmtYmd(s.startDate)} – ${fmtYmd(s.endDate)}`;
    const ariaLabel =
      `${s.label}${s.promoted ? ' (promoted)' : ''}, ${s.status || 'draft'}, ` +
      `${range}, ${s.days} day${s.days === 1 ? '' : 's'}`;

    return (
      <div
        key={s.id}
        className={styles.segment}
        ref={(el) => {
          segRefs.current[s.id] = el;
        }}
        style={{ flexGrow: s.flexGrow, borderWidth: '1.5px', borderStyle: 'solid', borderColor: accent }}
        role="img"
        aria-label={ariaLabel}
      >
        {/* Grip caps imply draggability — only show them when the boundaries are
            actually editable. Locked (read-only) mode reads as a clean bordered
            capsule; the accent border still carries status. */}
        {editable && (
          <span className={styles.cap} style={{ backgroundColor: accent }} aria-hidden>
            <span className={styles.gripPill} />
          </span>
        )}
        <div className={styles.body}>
          <span className={styles.segmentLabel}>
            {s.promoted && <LockClosed12Filled className={styles.lock} aria-hidden />}
            {s.label}
          </span>
          <span className={styles.segmentMeta}>{s.days}d</span>
        </div>
        {editable && (
          <span className={styles.cap} style={{ backgroundColor: accent }} aria-hidden>
            <span className={styles.gripPill} />
          </span>
        )}
        {playhead !== null && (
          <span className={styles.playhead} style={{ left: `${playhead}%` }}>
            <span className={styles.playheadDot} />
          </span>
        )}
      </div>
    );
  };

  return (
    <div className={styles.wrap} role="group" aria-label="Project schedule timeline">
      <div className={styles.track}>
        {timeline.segments.map((s, i) => {
          // boundaries[i] is segment i's LEFT edge: project start (i === 0) or the
          // seam shared with the previous segment. Its controlling segment (whose
          // duration the drag changes) is the previous one — or segment 0 itself
          // for the project start.
          const leftBoundary = boundaries[i];
          const prev = timeline.segments[i - 1];
          const leftLabel =
            i === 0
              ? `Project start, ${fmtYmd(leftBoundary.date)}`
              : `${prev.label} / ${s.label} boundary`;
          return (
            <React.Fragment key={s.id}>
              {renderBoundary(leftBoundary, i === 0 ? s : prev, leftLabel)}
              {renderSegment(s)}
            </React.Fragment>
          );
        })}
        {/* Trailing boundary = the last segment's right edge (project end). */}
        {renderBoundary(
          boundaries[timeline.segments.length],
          timeline.segments[timeline.segments.length - 1],
          `Project end, ${fmtYmd(boundaries[timeline.segments.length].date)}`,
        )}
        {/* Pickup-only phases (the final pickup) cap the strip as terminal markers
            rather than proportional segments. */}
        {timeline.markers.map((m) => {
          const accent = accentFor(m.status);
          return (
            <div
              key={m.id}
              className={styles.marker}
              role="img"
              aria-label={`${m.label}, pickup ${fmtYmd(m.date)}`}
            >
              <span className={styles.markerPin} style={{ borderColor: accent, color: accent }}>
                <VehicleTruck20Filled />
              </span>
              <span className={styles.markerLabel}>{fmtYmd(m.date)}</span>
            </div>
          );
        })}
      </div>
      <div className={styles.axis}>
        <Text size={200}>{timeline.domainStart && fmtYmd(timeline.domainStart)}</Text>
        <Text size={200} className={styles.axisCenter}>
          {timeline.totalDays} day{timeline.totalDays === 1 ? '' : 's'} total
          {timeline.skippedCount > 0 &&
            ` · ${timeline.skippedCount} phase${
              timeline.skippedCount === 1 ? '' : 's'
            } not yet scheduled`}
        </Text>
        <Text size={200}>{timeline.domainEnd && fmtYmd(timeline.domainEnd)}</Text>
      </div>
    </div>
  );
};

export default PhaseTimelineStrip;
